import {
  closestCenter,
  DndContext,
  DndContextProps,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FileMagnifyingGlassIcon } from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DraggableRow, LineLoader, RingLoader, Text } from '@components';
import { ApiEndpoint, FormType, Translation } from '@enums';
import { FormHandlers, useAppDispatch } from '@hooks';
import { updateSortingList } from '@store';
import type { Column, ColumnLayout, DraggableItem, FormMetadata, PaginationProps, SortableFormItem } from '@types';

import { HeaderCell } from './DraggableHeaderCell';
import Pagination from './Pagination';

interface DraggableContainerProps<T extends DraggableItem> extends FormMetadata {
  rows: T[];
  columns: Column[];
  actionHandlers?: FormHandlers<T>;
  isDraggable?: boolean;
  isDataLoading?: boolean;
  pagination?: PaginationProps;
  onChangePage?: (page: number) => void;
  onChangePageSize?: (page_size: number) => void;
}

export const DraggableTable = memo(
  <T extends DraggableItem>({
    rows: initialItems,
    formType = FormType.Empty,
    endpoint = ApiEndpoint.DEFAULT,
    actionHandlers,
    columns,
    isDraggable = false,
    isDataLoading = false,
    pagination,
    onChangePage,
    onChangePageSize,
  }: DraggableContainerProps<T>) => {
    const dispatch = useAppDispatch();
    const [rows, setRows] = useState<SortableFormItem[] | any[]>(initialItems);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    const { t: tTable } = useTranslation(Translation.Common, { keyPrefix: 'tables' });

    const columnLayouts = useMemo(() => {
      const availableWidth = containerWidth || 0;

      const totalMinWidth = columns.reduce((acc, col) => {
        const minWidth = col.minWidth ? parseInt(col.minWidth.toString()) : col.width ?? 100;
        return acc + minWidth;
      }, 0);

      if (totalMinWidth > availableWidth || availableWidth < 640) {
        return columns.map((col): ColumnLayout => {
          const minWidth = col.minWidth ? parseInt(col.minWidth.toString()) : col.width ?? 100;

          return {
            width: `${minWidth}px`,
            minWidth: `${minWidth}px`,
            maxWidth: col.maxWidth ? `${col.maxWidth}px` : undefined,
            flex: '0 0 auto',
            percentage: (minWidth / totalMinWidth) * 100,
          };
        });
      }

      const totalDefinedWidth = columns.reduce((acc, col) => acc + (col.width || 0), 0);

      if (totalDefinedWidth > 0 && totalDefinedWidth < availableWidth) {
        const remainingWidth = availableWidth - totalDefinedWidth;
        const scale = availableWidth / totalDefinedWidth;

        return columns.map((col): ColumnLayout => {
          const width = col.width ? col.width * scale : remainingWidth / columns.length;
          const percentage = (width / availableWidth) * 100;

          return {
            width: `${width}px`,
            minWidth: col.minWidth ? `${col.minWidth}px` : `${Math.min(width, 100)}px`,
            maxWidth: col.maxWidth ? `${col.maxWidth}px` : undefined,
            flex: '1 1 auto',
            percentage,
          };
        });
      }

      const equalWidth = availableWidth / columns.length;
      return columns.map((col): ColumnLayout => {
        const minWidth = col.minWidth ? parseInt(col.minWidth.toString()) : 100;

        return {
          width: `${equalWidth}px`,
          minWidth: `${minWidth}px`,
          maxWidth: col.maxWidth ? `${col.maxWidth}px` : undefined,
          flex: '1',
          percentage: 100 / columns.length,
        };
      });
    }, [columns, containerWidth]);

    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: { distance: 8 },
      }),
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 250,
          tolerance: 5,
          distance: 8,
        },
      }),
      useSensor(KeyboardSensor),
    );

    useEffect(() => {
      setRows(initialItems);
    }, [initialItems]);

    const handleDragEnd: DndContextProps['onDragEnd'] = useCallback((event) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        setRows((prevItems) => {
          const oldIndex = prevItems.findIndex((item) => item.id === active.id);
          const newIndex = prevItems.findIndex((item) => item.id === over.id);

          const newItems: SortableFormItem[] = [...prevItems];
          const [movedItem] = newItems.splice(oldIndex, 1);
          newItems.splice(newIndex, 0, movedItem);
          dispatch(updateSortingList({ formType, list: newItems }));

          return newItems;
        });
      }
    }, []);

    return (
      <div
        ref={containerRef}
        className="relative flex flex-col overflow-hidden rounded-2xl ring-1 ring-theme-text-secondary"
        style={{ touchAction: isDraggable ? 'none' : 'auto' }}
      >
        <div className={`flex-1 ${isDraggable ? '' : 'overflow-x-auto padded-horizontal-scrollbar-track'}`}>
          <div className="min-w-fit max-w-full">
            <div className="sticky top-0 z-10 flex bg-gray-50 bg-opacity-90 border-b">
              {columns?.map((col, index) => (
                <HeaderCell
                  formType={formType}
                  endpoint={endpoint}
                  key={`col-${index}`}
                  colId={index}
                  column={col}
                  layout={columnLayouts[index]}
                />
              ))}
            </div>
            {!!isDataLoading && (
              <div className="relative h-1">
                <div className="absolute w-full top-0 left-0 bg-gray-50">
                  <LineLoader size="100%" stroke={3.5} color="#417788" loading={isDataLoading} />
                </div>
              </div>
            )}
            <div className="min-w-0">
              {rows?.length ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={rows?.filter((item) => item?.id)?.map((item) => item.id) || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {rows?.map((item, index) => (
                      <DraggableRow
                        key={`${item.id}-${index}`}
                        item={item}
                        index={index}
                        endpoint={endpoint}
                        columns={columns}
                        pagination={pagination}
                        columnLayouts={columnLayouts}
                        isDraggable={isDraggable}
                        actionHandlers={actionHandlers}
                        formType={formType}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : isDataLoading ? (
                <div className="h-16 flex items-center justify-center">
                  <Text $level={4} $customizeColor className="flex justify-center items-center gap-x-4 text-gray-300">
                    <RingLoader color="#36d7b7" size={15} />
                    {tTable('fetchingData')}
                  </Text>
                </div>
              ) : (
                <div className="h-16 flex items-center justify-center select-none">
                  <Text $level={4} $customizeColor className="flex justify-center items-center gap-4 text-gray-300">
                    <FileMagnifyingGlassIcon className="size-6 text-gray-300" />
                    {tTable('noRecordFound')}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
        {!!pagination?.total && onChangePage && (
          <div className="sticky bottom-0 border-t">
            <Pagination pagination={pagination} onChangePageSize={onChangePageSize} onChangePage={onChangePage} />
          </div>
        )}
      </div>
    );
  },
);
